
import React, { useState, useMemo, useEffect } from 'react';
import { 
  Plus, Trash2, Edit3, ArrowRight, Sparkles, Home, Check, ChevronLeft, 
  AlertCircle, X, DollarSign, Settings2, HardHat, BrickWall, Zap, Paintbrush, 
  Trees, FileText, Hammer, CheckCircle, Loader2, Lock, Calculator
} from 'lucide-react';
import { Stage, BudgetMode, PlanTier } from '../types';
import { CONSTRUCTION_STAGES } from '../constants';
import { currencyMask, parseCurrency, formatCurrency } from '../utils/formatters';

interface StagesSetupProps {
  onComplete: (stages: Stage[], budgetMode: BudgetMode, generalBudget?: number) => void;
  onBack: () => void;
  projectName: string;
  budgetMode?: BudgetMode;
  initialStages?: Stage[];
  initialBudget?: number;
  isLoading?: boolean;
  planTier: PlanTier;
  onUpgrade: () => void;
}

const ICON_MAP: Record<string, React.ElementType> = {
  'HardHat': HardHat,
  'BrickWall': BrickWall,
  'Home': Home,
  'Zap': Zap,
  'Paintbrush': Paintbrush,
  'Trees': Trees,
  'FileText': FileText,
  'Hammer': Hammer,
  'CheckCircle': CheckCircle,
  'Droplets': Zap
};

export const StagesSetup: React.FC<StagesSetupProps> = ({ 
  onComplete, 
  onBack, 
  projectName, 
  budgetMode = 'general',
  initialStages,
  initialBudget = 0,
  isLoading = false,
  planTier,
  onUpgrade
}) => {
  const [stages, setStages] = useState<Stage[]>(initialStages && initialStages.length > 0 ? initialStages : CONSTRUCTION_STAGES);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [mode, setMode] = useState<BudgetMode>(budgetMode);
  
  const isDetailedMode = mode === 'detailed';
  const allowDetailed = planTier === 'pro_plus'; 
  
  const [generalBudgetInput, setGeneralBudgetInput] = useState(initialBudget > 0 ? currencyMask(initialBudget.toFixed(2).replace('.', '')) : '');
  const [editName, setEditName] = useState("");
  const [editBudget, setEditBudget] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    if (initialStages && initialStages.length > 0) {
      setStages(initialStages);
    }
  }, [initialStages]);

  const totalBudget = useMemo(() => {
    if (isDetailedMode) {
        return stages.reduce((acc, stage) => acc + (stage.expectedBudget || 0), 0);
    } else {
        return parseCurrency(generalBudgetInput) || 0;
    }
  }, [stages, isDetailedMode, generalBudgetInput]);

  const addStage = () => {
    if (editingId) saveEdit();
    const newStage: Stage = {
      id: Math.random().toString(36).substr(2, 9),
      name: '', 
      expectedBudget: 0,
      iconName: 'Home'
    };
    setStages([...stages, newStage]);
    setEditingId(newStage.id);
    setEditName("");
    setEditBudget("R$ 0,00");
  };

  const removeStage = (id: string) => {
    if (stages.length <= 1) {
      setError(true);
      setTimeout(() => setError(false), 3000);
      return;
    }
    setStages(stages.filter(s => s.id !== id));
    if (editingId === id) setEditingId(null);
  };

  const startEdit = (stage: Stage) => {
    setEditingId(stage.id);
    setEditName(stage.name);
    setEditBudget(currencyMask(stage.expectedBudget.toFixed(2).replace(".", "")));
  };

  const cancelEdit = (id: string) => {
    const stage = stages.find(s => s.id === id);
    if (stage && stage.name === '') removeStage(id);
    setEditingId(null);
  };

  const saveEdit = () => {
    if (!editName.trim()) return;
    const budgetValue = isDetailedMode ? parseCurrency(editBudget) : 0;
    setStages(stages.map(s => s.id === editingId ? { ...s, name: editName, expectedBudget: budgetValue } : s));
    setEditingId(null);
  };

  const handleModeToggle = () => {
    if (allowDetailed) {
        setMode(prev => prev === 'general' ? 'detailed' : 'general');
    } else {
        onUpgrade();
    }
  };
  
  const handleFinish = () => {
      const finalGeneralBudget = mode === 'general' ? parseCurrency(generalBudgetInput) : undefined;
      onComplete(stages, mode, finalGeneralBudget);
  };

  return (
    <div className="min-h-screen mesh-gradient flex flex-col px-8 pt-12 pb-10 animate-fadeIn overflow-y-auto">
      <div className="mb-8 flex items-center gap-4">
        <button onClick={onBack} disabled={isLoading} className="p-2 bg-white dark:bg-slate-900 rounded-full shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-50">
          <ChevronLeft size={20} className="text-slate-500" />
        </button>
        <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 w-full rounded-full transition-all duration-700 shadow-[0_0_10px_rgba(16,185,129,0.3)]"></div>
        </div>
        <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Configuração</span>
      </div>

      <div className="mb-6">
        <div className="flex justify-between items-start">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 dark:bg-emerald-800/30 rounded-full text-[9px] font-black text-emerald-800 dark:text-emerald-400 uppercase tracking-widest mb-4">
               <Sparkles size={10} /> {isDetailedMode ? 'Orçamento Detalhado' : 'Orçamento Simplificado'}
            </div>
            <button 
              onClick={handleModeToggle} 
              className={`flex items-center gap-3 px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-md hover:scale-105 ${
                isDetailedMode 
                  ? 'bg-slate-900 text-white' 
                  : (allowDetailed ? 'bg-gradient-to-r from-indigo-500 to-indigo-700 text-white animate-pulse' : 'bg-slate-200 dark:bg-slate-800 text-slate-500 cursor-pointer')
              }`}
            >
              {isDetailedMode ? <Calculator size={14} /> : (allowDetailed ? <Settings2 size={14} /> : <Lock size={14} />)} 
              {isDetailedMode ? 'Voltar ao Simples' : (allowDetailed ? 'Ativar Detalhado' : 'Bloqueado')}
            </button>
        </div>

        <h1 className="text-4xl font-black text-emerald-900 dark:text-white tracking-tighter leading-none mb-4">
          Defina as <br/><span className="text-emerald-600">Etapas</span>
        </h1>
        
        <div className="mt-2 p-6 bg-white dark:bg-slate-900 rounded-[2rem] border border-emerald-100 dark:border-emerald-900/30 shadow-lg flex justify-between items-center animate-fadeIn relative overflow-hidden">
             <div className="absolute top-0 right-0 p-4 opacity-5"><Calculator size={64}/></div>
             <div className="relative z-10 w-full">
                 <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">
                    {isDetailedMode ? 'Somatório Automático' : 'Orçamento Geral'}
                 </p>
                 
                 {isDetailedMode ? (
                     <div className="text-3xl font-black text-indigo-600 dark:text-indigo-400 tracking-tight">
                        {formatCurrency(totalBudget)}
                     </div>
                 ) : (
                     <div className="relative">
                        <input 
                           type="text"
                           inputMode="numeric"
                           className="w-full text-3xl font-black text-emerald-600 dark:text-emerald-400 bg-transparent outline-none placeholder-emerald-200 tracking-tight"
                           value={generalBudgetInput}
                           onChange={(e) => setGeneralBudgetInput(currencyMask(e.target.value))}
                           placeholder="R$ 0,00"
                        />
                        <Edit3 size={16} className="absolute right-0 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
                     </div>
                 )}
             </div>
        </div>
      </div>

      <div className="space-y-3 flex-1 overflow-y-auto max-h-[50vh] pr-2 custom-scrollbar mb-4 pb-20">
        {stages.map((stage) => {
          const IconComponent = ICON_MAP[stage.iconName || 'Home'] || Home;
          return (
            <div key={stage.id} className="relative group">
              {editingId === stage.id ? (
                <div className="bg-white dark:bg-slate-950 p-4 rounded-2xl border-2 border-emerald-500 shadow-xl animate-fadeIn space-y-3">
                  <input 
                    autoFocus
                    placeholder="Nome da etapa..."
                    className="w-full bg-slate-50 dark:bg-slate-900 border-none rounded-xl px-4 py-3 text-sm font-black text-emerald-900 dark:text-white outline-none uppercase tracking-tight"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                  />
                  {isDetailedMode && (
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-600"><DollarSign size={14} /></div>
                      <input 
                          placeholder="R$ 0,00"
                          className="w-full bg-slate-50 dark:bg-slate-900 border-none rounded-xl pl-10 pr-4 py-3 text-lg font-bold text-slate-900 dark:text-white outline-none"
                          value={editBudget}
                          onChange={(e) => setEditBudget(currencyMask(e.target.value))}
                      />
                    </div>
                  )}
                  <div className="flex gap-2">
                      <button onClick={saveEdit} className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-black text-xs uppercase tracking-widest">Salvar</button>
                      <button onClick={() => cancelEdit(stage.id)} className="px-4 py-3 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-xl"><X size={18} /></button>
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl flex items-center justify-between shadow-sm transition-all hover:border-emerald-200">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="size-10 shrink-0 bg-slate-50 dark:bg-slate-800 rounded-xl flex items-center justify-center text-slate-400"><IconComponent size={18} /></div>
                    <div className="flex flex-col min-w-0">
                        <span className="font-bold text-slate-700 dark:text-slate-200 text-sm uppercase tracking-tight truncate">{stage.name}</span>
                        {isDetailedMode && <span className="text-xs font-black text-indigo-600 dark:text-indigo-400">{formatCurrency(stage.expectedBudget)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(stage)} className="p-2 text-slate-300 hover:text-emerald-600"><Edit3 size={16}/></button>
                    <button onClick={() => removeStage(stage.id)} className="p-2 text-slate-300 hover:text-red-500"><Trash2 size={16}/></button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        <button onClick={addStage} className="w-full py-5 bg-white dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-700 rounded-[2rem] flex items-center justify-center gap-3 text-slate-500 font-black text-[10px] uppercase tracking-[0.2em] hover:border-emerald-400 hover:text-emerald-600 transition-all">
          <Plus size={14} strokeWidth={4} /> Adicionar Nova Etapa
        </button>
      </div>

      <div className="mt-auto pt-4 bg-gradient-to-t from-slate-50 to-transparent dark:from-slate-950 pb-6 -mx-8 px-8 sticky bottom-0">
        <button 
          onClick={handleFinish}
          disabled={isLoading}
          className="w-full bg-emerald-800 dark:bg-emerald-600 text-white py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-2xl flex items-center justify-center gap-3 active:scale-95 transition-all"
        >
          {isLoading ? <Loader2 size={18} className="animate-spin" /> : <>Finalizar Configuração <ArrowRight size={18} /></>}
        </button>
      </div>
    </div>
  );
};
